import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getSafeAvatarUrl, getSanitizedYouTubeThumbnail } from '../utils/validation';
import { socket } from '../socket';
import { getOrCreateIdentity, setIdentity as setIdentityStorage, setIdentityName, setIdentityAvatar } from '../utils/identity';
import { verifyStoredToken, getStoredToken, type AuthUser } from '../utils/auth';
import { fetchStagePrefs } from '../utils/profileApi';
import { fetchRoomPlaylists } from '../utils/roomPlaylistsApi';
import { useSocketRoom } from '../hooks/useSocketRoom';
import { useI18n } from '../i18n/useI18n';
import QueueItem from './QueueItem';
import SearchSheet from './SearchSheet';
import RoomJoinGate from './RoomJoinGate';
import type { QueueMessage, Playlist, VideoResult, ArtistSummary } from '../types';
import KaraokrLogo from './KaraokrLogo';
import ReactionIcon from './ReactionIcon';
import DuetInviteModal from './DuetInviteModal';
import DuetInviteToast from './DuetInviteToast';
import DuetResultToast, { type DuetResult } from './DuetResultToast';
import TurnToast, { type TurnNotice } from './TurnToast';
import { alertTurn } from '../utils/turnAlert';
import { FALLBACK_SONG_SECONDS, formatWaitTime } from '../utils/queue';
import { trackEvent } from '../analytics/posthog';
import StagePrefsModal from './StagePrefsModal';
import type { ClientInfo, DuetInvite } from '../types';

export default function SingerPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const [identity, setIdentity] = useState(getOrCreateIdentity());
  const [displayName, setDisplayName] = useState(identity.name);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [quickSong, setQuickSong] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [ytError, setYtError] = useState<string | null>(null);
  const [ytResults, setYtResults] = useState<VideoResult[]>([]);
  const [ytFromCache, setYtFromCache] = useState(false);
  const [artists, setArtists] = useState<ArtistSummary[]>([]);
  const [selectedArtist, setSelectedArtist] = useState<string | null>(null);
  const [artistSongs, setArtistSongs] = useState<VideoResult[] | null>(null);
  const [isLoadingArtistSongs, setIsLoadingArtistSongs] = useState(false);
  const artistsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [forceUsedQuery, setForceUsedQuery] = useState<string | null>(null);
  const [previewVideo, setPreviewVideo] = useState<VideoResult | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [showStagePrefs, setShowStagePrefs] = useState(false);
  const [favorites, setFavorites] = useState<VideoResult[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [savedSongs, setSavedSongs] = useState<VideoResult[]>([]);
  const [optimisticAdds, setOptimisticAdds] = useState<(QueueMessage & { optimistic?: boolean })[]>([]);
  const [optimisticRemovals, setOptimisticRemovals] = useState<Set<string>>(new Set());
  // Erro do último add (limite atingido, fila fechada, timeout): mostrado num
  // banner até ser dispensado. Antes o add falhava em silêncio e o singer
  // re-tentava sem perceber porquê (evento 12/10).
  const [addError, setAddError] = useState<string | null>(null);
  // Add pendente de confirmação do servidor: clientMessageId idempotente
  // (o retry reutiliza o mesmo id, pelo que o Queue Service nunca duplica).
  const pendingAddRef = useRef<{
    clientMessageId: string;
    payload: Record<string, unknown>;
    optimisticId: string;
    attempts: number;
    timer: ReturnType<typeof setTimeout> | null;
  } | null>(null);
  const [duetSingers, setDuetSingers] = useState<ClientInfo[]>([]);
  const [showDuetModal, setShowDuetModal] = useState(false);
  const [selectedDuetSongId, setSelectedDuetSongId] = useState<string | null>(null);
  const [duetToasts, setDuetToasts] = useState<DuetInvite[]>([]);
  const [duetResults, setDuetResults] = useState<DuetResult[]>([]);
  // Convites que EU enviei, a aguardar resposta (messageId -> destinatário/título).
  // Permite mostrar o toast de resultado apenas no singer que convidou.
  const pendingDuetInvitesRef = useRef<Map<string, { inviteeName: string; songTitle?: string }>>(new Map());
  const [turnNotices, setTurnNotices] = useState<TurnNotice[]>([]);
  // Ids das músicas já anunciadas em cada estado, para o aviso disparar só na
  // transição (não repetir a cada queue_update).
  const announcedNextRef = useRef<string | null>(null);
  const announcedOnRef = useRef<string | null>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const { t } = useI18n();
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAddingRef = useRef(false);

  const {
    queue,
    isHost,
    isQueueOpen,
    joinStatus,
    joinError,
    nowPlayingId,
    maxMessagesPerUser,
    blockedUsers,
    showFullQueue,
    doJoin,
    leave,
  } = useSocketRoom(roomId, identity, displayName);

  // Load auth identity when available.
  useEffect(() => {
    verifyStoredToken().then((auth) => {
      if (auth) {
        setAuthUser(auth.user);
        setDisplayName(auth.user.name);
        setIsAuthenticated(true);
        const nextIdentity = {
          id: auth.user.id,
          name: auth.user.name,
          avatarUrl: auth.user.avatar_url || '',
          email: auth.user.email || undefined,
        };
        setIdentity(nextIdentity);
        setIdentityStorage(nextIdentity);
        // If the room was joined before login, the gateway still holds the
        // pre-login (anonymous) userId while new queue songs are added with
        // the authenticated id — that mismatch makes the gateway reject duet
        // invites ("only the song owner"). Re-authenticate the live socket so
        // the gateway identity matches the identity used from here on.
        const token = getStoredToken();
        if (token) socket.emit('authenticate', { token });
        // A custom Stage name (set in Stage preferences) takes precedence
        // over the provider name for what this singer sends.
        fetchStagePrefs(auth.user.id).then((prefs) => {
          if (prefs?.displayName) setDisplayName(prefs.displayName);
        });
      }
    });
  }, []);

  // WebSocket listeners for search, recommendations and queue state
  useEffect(() => {
    const onYoutubeResults = (res: VideoResult[] | { results: VideoResult[]; fromCache: boolean }) => {
      if (Array.isArray(res)) {
        setYtResults(res);
        setYtFromCache(false);
      } else {
        setYtResults(res.results);
        setYtFromCache(res.fromCache);
      }
      setIsSearching(false);
      setYtError(null);
    };
    const onArtistsResults = (data: { artists?: ArtistSummary[] } | ArtistSummary[]) => {
      setArtists(Array.isArray(data) ? data : data?.artists ?? []);
    };
    const onArtistSongsResults = (data: { results?: VideoResult[] } | VideoResult[]) => {
      setArtistSongs(Array.isArray(data) ? data : data?.results ?? []);
      setIsLoadingArtistSongs(false);
    };
    const onYoutubeError = (msg: string) => {
      setIsSearching(false);
      setYtError(msg || t('search.youtubeSearchFailed'));
    };
    const onRecommendations = (data: { history: any[]; favorites: any[] }) => {
      const mappedFavorites = (data.favorites || []).map((f: any) => ({
        videoId: f.video_id as string,
        title: f.title as string,
        thumbnail: f.thumbnail as string,
      }));
      const mappedHistory = (data.history || []).map((h: any) => ({
        videoId: h.video_id as string,
        title: h.title as string,
        thumbnail: h.thumbnail as string,
      }));
      setFavorites(mappedFavorites);
      setSavedSongs(mappedFavorites);
      if (mappedHistory.length > 0) {
        setPlaylists([
          {
            id: 'recently-played',
            name: t('search.recentlyPlayed'),
            description: t('search.recentlyPlayedDescription'),
            songs: mappedHistory,
          },
        ]);
      }
    };

    const onSingersList = (data: { singers: ClientInfo[] }) => {
      setDuetSingers(data.singers || []);
    };
    const onDuetInviteReceived = (data: DuetInvite) => {
      setDuetToasts((prev) => {
        if (prev.some((i) => i.inviteId === data.inviteId)) return prev;
        return [...prev, data];
      });
    };
    // Result events (accepted/declined/expired) reach both parties. Only the
    // inviter has a pending entry for the messageId, so only they get a toast.
    const addDuetResult = (kind: DuetResult['kind'], data: { messageId?: string }) => {
      if (!data.messageId) return;
      const pending = pendingDuetInvitesRef.current.get(data.messageId);
      if (!pending) return;
      pendingDuetInvitesRef.current.delete(data.messageId);
      setDuetResults((prev) => [
        ...prev,
        {
          id: `${data.messageId}-${kind}`,
          kind,
          partnerName: pending.inviteeName,
          songTitle: pending.songTitle,
        },
      ]);
    };
    const onDuetInviteAccepted = (data: { messageId?: string }) => {
      addDuetResult('accepted', data);
    };
    const onDuetInviteDeclined = (data: { inviteId?: string; messageId?: string }) => {
      if (data.inviteId) removeDuetInvite(data.inviteId);
      addDuetResult('declined', data);
    };
    const onDuetInviteExpired = (data: { inviteId?: string; messageId?: string }) => {
      if (data.inviteId) removeDuetInvite(data.inviteId);
      addDuetResult('expired', data);
    };
    // Duet invite rejections arrive as generic queue_error messages (the
    // gateway has no dedicated invite_error type) and are otherwise swallowed
    // outside the join phase. If we have a pending invite, surface the failure
    // so the inviter is not left waiting for a toast that never comes.
    const onDuetInviteError = (msg: string) => {
      if (!msg) return;
      const isDuetError =
        msg.includes('duet') || msg.includes('song not found') || msg.includes('singer not available');
      if (!isDuetError) return;
      const pending = pendingDuetInvitesRef.current;
      const first = pending.entries().next().value as
        | [string, { inviteeName: string; songTitle?: string }]
        | undefined;
      if (!first) return;
      pending.delete(first[0]);
      setDuetResults((prev) => [
        ...prev,
        {
          id: `${first[0]}-failed`,
          kind: 'failed' as const,
          partnerName: first[1].inviteeName,
          songTitle: first[1].songTitle,
        },
      ]);
    };

    // Erros de add (limite atingido, fila fechada, bloqueado) chegam pelo
    // gateway com a mensagem real do Queue Service. Se há um add pendente,
    // falha-o com esse motivo em vez de deixar o singer sem feedback.
    const onAddError = (msg: string) => {
      if (!msg || !pendingAddRef.current) return;
      const isDuetError =
        msg.includes('duet') || msg.includes('song not found') || msg.includes('singer not available');
      if (isDuetError) return;
      failPendingAdd(msg);
    };

    socket.on('youtube_results', onYoutubeResults);
    socket.on('youtube_error', onYoutubeError);
    socket.on('artists_results', onArtistsResults);
    socket.on('artist_songs_results', onArtistSongsResults);
    socket.on('recommendations_data', onRecommendations);
    socket.on('singers_list', onSingersList);
    socket.on('duet_invite_received', onDuetInviteReceived);
    socket.on('duet_invite_accepted', onDuetInviteAccepted);
    socket.on('duet_invite_declined', onDuetInviteDeclined);
    socket.on('duet_invite_expired', onDuetInviteExpired);
    socket.on('queue_error', onDuetInviteError);
    socket.on('queue_error', onAddError);
    return () => {
      socket.off('youtube_results', onYoutubeResults);
      socket.off('youtube_error', onYoutubeError);
      socket.off('artists_results', onArtistsResults);
      socket.off('artist_songs_results', onArtistSongsResults);
      socket.off('recommendations_data', onRecommendations);
      socket.off('singers_list', onSingersList);
      socket.off('duet_invite_received', onDuetInviteReceived);
      socket.off('duet_invite_accepted', onDuetInviteAccepted);
      socket.off('duet_invite_declined', onDuetInviteDeclined);
      socket.off('duet_invite_expired', onDuetInviteExpired);
      socket.off('queue_error', onDuetInviteError);
      socket.off('queue_error', onAddError);
    };
  }, []);

  // Fetch recommendations when search opens if authenticated and fully joined
  useEffect(() => {
    if (showSearch && isAuthenticated && joinStatus === 'ready') {
      socket.emit('get_recommendations');
    }
  }, [showSearch, isAuthenticated, joinStatus]);

  // Fetch room playlists when search opens.
  useEffect(() => {
    if (!showSearch || !roomId) return;
    let cancelled = false;
    fetchRoomPlaylists(roomId)
      .then((data) => {
        if (!cancelled) {
          setPlaylists(
            data.map((p) => ({
              id: p.id,
              name: p.name,
              songs: (p.items || []).map((item) => ({
                videoId: item.video_id,
                title: item.title,
                thumbnail: item.thumbnail || '',
              })),
            }))
          );
        }
      })
      .catch((err) => {
        console.error('[SingerPage] Failed to load room playlists:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [showSearch, roomId]);

  // Close search sheet when queue is closed by host or singer is blocked
  useEffect(() => {
    if ((!isQueueOpen || blockedUsers.includes(identity.id)) && showSearch) {
      setShowSearch(false);
      setQuickSong('');
      setYtResults([]);
      setYtError(null);
      setPreviewVideo(null);
    }
  }, [isQueueOpen, blockedUsers, identity.id, showSearch]);

  // Debounced YouTube search
  useEffect(() => {
    if (!quickSong.trim()) {
      setYtResults([]);
      setYtFromCache(false);
      setYtError(null);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      socket.emit('search_youtube', { query: quickSong.trim() });
    }, 500);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [quickSong]);

  // Artist row: top artists when the sheet opens, debounced re-search as the
  // user types (same 500ms pattern as the YouTube search above).
  useEffect(() => {
    if (!showSearch) return;
    const query = quickSong.trim();
    if (!query) {
      socket.emit('search_artists', { query: '' });
      return;
    }
    if (artistsDebounceRef.current) clearTimeout(artistsDebounceRef.current);
    artistsDebounceRef.current = setTimeout(() => {
      socket.emit('search_artists', { query });
    }, 500);
    return () => {
      if (artistsDebounceRef.current) clearTimeout(artistsDebounceRef.current);
    };
  }, [showSearch, quickSong]);

  const handleSelectArtist = useCallback((artist: string) => {
    setSelectedArtist(artist);
    setArtistSongs(null);
    setIsLoadingArtistSongs(true);
    socket.emit('get_artist_songs', { artist });
  }, []);

  const handleClearArtist = useCallback(() => {
    setSelectedArtist(null);
    setArtistSongs(null);
  }, []);

  const handleForceYouTubeSearch = useCallback(() => {
    const query = quickSong.trim();
    if (!query) return;
    setIsSearching(true);
    setYtResults([]);
    setYtFromCache(false);
    setYtError(null);
    setForceUsedQuery(query);
    socket.emit('search_youtube', { query, force: true });
  }, [quickSong]);

  // Remove optimistic adds once the server confirms a matching song.
  useEffect(() => {
    if (optimisticAdds.length === 0) return;
    setOptimisticAdds((prev) => {
      const remaining = prev.filter(
        (o) =>
          !queue.some(
            (m) =>
              m.userId === o.userId &&
              m.metadata?.videoId === o.metadata?.videoId &&
              m.content === o.content
          )
      );
      return remaining.length === prev.length ? prev : remaining;
    });
  }, [queue]);

  // Qualquer atualização de fila enquanto há um add pendente significa que o
  // servidor processou o pedido (com sucesso ou com erro já recebido via
  // queue_error): resolve o pending e cancela o retry/timeout.
  useEffect(() => {
    const pending = pendingAddRef.current;
    if (!pending) return;
    if (pending.timer) clearTimeout(pending.timer);
    pendingAddRef.current = null;
  }, [queue]);

  // Dispensar o banner de erro automaticamente ao fim de 6s.
  useEffect(() => {
    if (!addError) return;
    const t = setTimeout(() => setAddError(null), 6000);
    return () => clearTimeout(t);
  }, [addError]);

  // Stop filtering removed items once the server queue no longer contains them.
  useEffect(() => {
    if (optimisticRemovals.size === 0) return;
    setOptimisticRemovals((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of prev) {
        if (!queue.some((m) => m.id === id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [queue]);

  // O dedup de optimistics acontece aqui no memo (e não só no useEffect
  // abaixo) para nunca renderizar um frame com a música duplicada: quando o
  // servidor confirma, a versão real substitui a optimistic na mesma render.
  const effectiveQueue = useMemo(() => {
    const matchesOptimistic = (o: QueueMessage) =>
      queue.some(
        (m) =>
          m.userId === o.userId &&
          m.metadata?.videoId === o.metadata?.videoId &&
          m.content === o.content
      );
    const confirmed = queue.filter((m) => !optimisticRemovals.has(m.id));
    const pending = optimisticAdds.filter((o) => !matchesOptimistic(o));
    return [...confirmed, ...pending];
  }, [queue, optimisticAdds, optimisticRemovals]);

  // A song co-sung via an accepted duet invite also belongs to the invitee's list.
  const isDuetPartner = useCallback(
    (m: { userId: string; partnerIds?: string[] }) =>
      !!m.partnerIds && m.partnerIds.includes(identity.id),
    [identity.id]
  );

  // O host controla se os singers veem a fila completa (showFullQueue). Com a
  // opção desligada, o singer vê só o now-playing e as próprias músicas
  // (mesmo padrão do visibleQueue do RoomPage).
  const visibleQueue = useMemo(
    () =>
      showFullQueue
        ? effectiveQueue
        : effectiveQueue.filter(
            (m) => m.id === nowPlayingId || m.userId === identity.id || isDuetPartner(m)
          ),
    [showFullQueue, effectiveQueue, nowPlayingId, identity.id, isDuetPartner]
  );

  // Estimativa de espera até à próxima música própria: soma das durações de
  // tudo o que está à frente dela na fila (inclui o now-playing inteiro —
  // o SingerPage não tem o progresso live do Stage). Só é calculada quando o
  // host mostra a fila completa; músicas sem duração usam o fallback.
  const waitEstimate = useMemo(() => {
    if (!showFullQueue) return null;
    const idx = effectiveQueue.findIndex((m) => m.userId === identity.id || isDuetPartner(m));
    if (idx <= 0) return null;
    let seconds = 0;
    for (let i = 0; i < idx; i++) {
      seconds += effectiveQueue[i].metadata?.duration ?? FALLBACK_SONG_SECONDS;
    }
    return seconds > 0 ? seconds : null;
  }, [showFullQueue, effectiveQueue, identity.id, isDuetPartner]);
  const waitTimeMessageId =
    waitEstimate !== null
      ? effectiveQueue.find((m) => m.userId === identity.id || isDuetPartner(m))?.id ?? null
      : null;

  const mySongsCount = useMemo(
    () => effectiveQueue.filter((m) => m.userId === identity.id || isDuetPartner(m)).length,
    [effectiveQueue, identity.id, isDuetPartner]
  );
  const atLimit = mySongsCount >= maxMessagesPerUser;
  const isBlocked = blockedUsers.includes(identity.id);
  const nowPlaying = useMemo(() => queue.find((m) => m.id === nowPlayingId) || null, [queue, nowPlayingId]);
  const isOwnSongOnStage = !!nowPlaying && (nowPlaying.userId === identity.id || isDuetPartner(nowPlaying));

  // Aviso de vez: "és o próximo" quando a minha primeira música passa a cabeça
  // da fila (e há alguém no palco — sem now playing o evento ainda não começou)
  // e "é agora" quando a minha música entra em now playing. Dispara só na
  // transição; duet partners também contam (a música é deles também).
  const removeTurnNotice = useCallback((id: string) => {
    setTurnNotices((prev) => prev.filter((n) => n.id !== id));
  }, []);
  useEffect(() => {
    announcedNextRef.current = null;
    announcedOnRef.current = null;
  }, [identity.id]);
  useEffect(() => {
    const isMine = (m: { userId: string; partnerIds?: string[] }) =>
      m.userId === identity.id || isDuetPartner(m);

    if (nowPlaying && isMine(nowPlaying)) {
      if (announcedOnRef.current !== nowPlaying.id) {
        announcedOnRef.current = nowPlaying.id;
        announcedNextRef.current = null;
        const id = `on-${nowPlaying.id}`;
        setTurnNotices((prev) =>
          prev.some((n) => n.id === id)
            ? prev
            : [...prev, { id, kind: 'on' as const, songTitle: nowPlaying.metadata?.title || nowPlaying.content }]
        );
        alertTurn();
      }
      return;
    }

    if (!nowPlayingId) return;
    const upcoming = effectiveQueue.filter((m) => m.id !== nowPlayingId);
    const myNext = upcoming.find(isMine);
    if (myNext && upcoming[0]?.id === myNext.id && announcedNextRef.current !== myNext.id) {
      announcedNextRef.current = myNext.id;
      const id = `next-${myNext.id}`;
      setTurnNotices((prev) =>
        prev.some((n) => n.id === id)
          ? prev
          : [...prev, { id, kind: 'next' as const, songTitle: myNext.metadata?.title || myNext.content }]
      );
      alertTurn();
    }
  }, [effectiveQueue, nowPlayingId, nowPlaying, identity.id, isDuetPartner]);

  // Falha o add pendente: remove o item optimistic, mostra o motivo real
  // (mensagem do Queue Service via gateway) e liberta o pending.
  const failPendingAdd = (message: string) => {
    const pending = pendingAddRef.current;
    if (pending?.timer) clearTimeout(pending.timer);
    if (pending) {
      setOptimisticAdds((prev) => prev.filter((o) => o.id !== pending.optimisticId));
    }
    pendingAddRef.current = null;
    setAddError(message);
  };

  const ADD_TIMEOUT_MS = 7000;
  const handleAddTimeout = () => {
    const pending = pendingAddRef.current;
    if (!pending) return;
    if (pending.attempts < 1) {
      // Retry com o MESMO clientMessageId: o Queue Service deduplica, pelo
      // que um ack perdido nunca cria uma música duplicada (evento 12/10).
      pending.attempts += 1;
      socket.emit('send_message', pending.payload);
      pending.timer = setTimeout(handleAddTimeout, ADD_TIMEOUT_MS);
    } else {
      failPendingAdd(t('singer.addFailed'));
    }
  };

  const addVideo = (video: VideoResult) => {
    if (!roomId || isAddingRef.current) return;
    isAddingRef.current = true;
    const latestIdentity = getOrCreateIdentity();
    const finalName = displayName.trim() || identity.name || latestIdentity.name || t('singer.guest');
    const finalUserId = identity.id || latestIdentity.id;
    const finalAvatar = identity.avatarUrl || latestIdentity.avatarUrl || undefined;

    const optimistic: QueueMessage & { optimistic?: boolean } = {
      id: `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      userId: finalUserId,
      userName: finalName,
      avatarUrl: finalAvatar,
      content: video.title,
      metadata: video,
      timestamp: Date.now(),
      optimistic: true,
    };
    setOptimisticAdds((prev) => [...prev, optimistic]);

    // Um UUID por tentativa de adição: retries reutilizam-no (dedup no
    // servidor); um novo clique gera um UUID novo e permite duplicar
    // deliberadamente.
    const payload = {
      roomId,
      userId: finalUserId,
      userName: finalName,
      avatarUrl: finalAvatar,
      content: video.title,
      metadata: video,
      clientMessageId: crypto.randomUUID(),
    };
    pendingAddRef.current = {
      clientMessageId: payload.clientMessageId,
      payload,
      optimisticId: optimistic.id,
      attempts: 0,
      timer: setTimeout(handleAddTimeout, ADD_TIMEOUT_MS),
    };
    socket.emit('send_message', payload);
    trackEvent('song_added', { roomId, videoId: video.videoId });
    setIdentityName(finalName);
    if (latestIdentity.avatarUrl) setIdentityAvatar(latestIdentity.avatarUrl);
    setShowSearch(false);
    setQuickSong('');
    setYtResults([]);
    setYtError(null);
    setPreviewVideo(null);
    setTimeout(() => {
      isAddingRef.current = false;
    }, 1000);
  };

  // Optimistic favorite toggle: update local state immediately so the star
  // and the Saved tab react instantly; persistence happens via the socket
  // event in the background and is reconciled on the next recommendations fetch.
  const toggleFavorite = (video: VideoResult, isFavorite: boolean) => {
    if (!video.videoId || !socket.connected) return;
    setFavorites((prev) =>
      isFavorite
        ? prev.some((f) => f.videoId === video.videoId)
          ? prev
          : [...prev, video]
        : prev.filter((f) => f.videoId !== video.videoId)
    );
    setSavedSongs((prev) =>
      isFavorite
        ? prev.some((f) => f.videoId === video.videoId)
          ? prev
          : [...prev, video]
        : prev.filter((f) => f.videoId !== video.videoId)
    );
    socket.emit('toggle_favorite', {
      videoId: video.videoId,
      title: video.title,
      thumbnail: video.thumbnail,
      isFavorite,
    });
  };

  const openDuetModal = (messageId: string) => {
    if (!roomId) return;
    setSelectedDuetSongId(messageId);
    socket.emit('list_singers', { roomId });
    setShowDuetModal(true);
  };

  const sendDuetInvite = (inviteeId: string, inviteeName: string) => {
    if (!roomId || !selectedDuetSongId) return;
    const songTitle = queue.find((m) => m.id === selectedDuetSongId)?.metadata?.title;
    pendingDuetInvitesRef.current.set(selectedDuetSongId, { inviteeName, songTitle });
    socket.emit('invite_duet', {
      roomId,
      messageId: selectedDuetSongId,
      inviteeId,
    });
    trackEvent('duet_invited', { roomId });
    setShowDuetModal(false);
    setSelectedDuetSongId(null);
  };

  const acceptDuet = (inviteId: string) => {
    if (!roomId) return;
    socket.emit('accept_duet', { roomId, inviteId });
    trackEvent('duet_accepted', { roomId });
    setDuetToasts((prev) => prev.filter((i) => i.inviteId !== inviteId));
  };

  const declineDuet = (inviteId: string) => {
    if (!roomId) return;
    socket.emit('decline_duet', { roomId, inviteId });
    setDuetToasts((prev) => prev.filter((i) => i.inviteId !== inviteId));
  };

  const removeDuetInvite = useCallback((inviteId: string) => {
    setDuetToasts((prev) => prev.filter((i) => i.inviteId !== inviteId));
  }, []);

  const removeDuetResult = useCallback((id: string) => {
    setDuetResults((prev) => prev.filter((r) => r.id !== id));
  }, []);

  if (joinStatus !== 'ready') {
    return (
      <RoomJoinGate
        roomId={roomId || ''}
        mode="singer"
        isJoining={joinStatus === 'joining'}
        error={joinError}
        onJoin={({ name, password }) => {
          setDisplayName(name);
          doJoin(name, password);
        }}
      />
    );
  }

  if (isHost) {
    return (
      <div className="landing-page">
        <div className="room-hero-glow" aria-hidden="true" />
        <div className="landing-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div className="card" style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}>{t('singer.hostRedirectTitle')}</h1>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>{t('singer.hostRedirectHint')}</p>
            <Link to={`/room/${roomId}`}>
              <button className="btn-primary" style={{ width: '100%' }}>{t('singer.goToHostPage')}</button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page">
      <div className="room-hero-glow" aria-hidden="true" />
      {addError && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#c0392b',
            color: '#fff',
            padding: '10px 16px',
            borderRadius: 8,
            zIndex: 9999,
            maxWidth: '92vw',
            boxShadow: '0 4px 12px rgba(0,0,0,.35)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 14,
          }}
        >
          <span>{addError}</span>
          <button
            onClick={() => setAddError(null)}
            aria-label="Dismiss"
            style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, padding: 0 }}
          >
            ✕
          </button>
        </div>
      )}
      <div className="landing-content">
        <div className="room-page">
          <div className="room-page-inner">
            <header className="room-header-bar">
              <div className="room-header-brand">
                <KaraokrLogo className="room-header-logo" />
                <div className="room-header-info">
                  <div className="room-header-name">{roomId}</div>
                  <div className="room-header-role">{t('singer.singerRole')}</div>
                </div>
              </div>
              <div className="room-header-actions">
                <span className={`room-badge ${isQueueOpen ? 'room-badge--open' : 'room-badge--closed'}`}>
                  {isQueueOpen ? t('singer.queueOpen') : t('singer.queueClosed')}
                </span>
                <div style={{ position: 'relative' }}>
                  <button className="room-avatar-btn" onClick={() => setDropdownOpen((v) => !v)} aria-label={t('singer.openMenu')}>
                    <img src={getSafeAvatarUrl(identity.avatarUrl, displayName || identity.name)} alt="" />
                  </button>
                  {dropdownOpen && (
                    <div className="room-dropdown">
                      <div className="room-dropdown-header">
                        <div className="room-dropdown-name">{displayName || identity.name || t('singer.guest')}</div>
                        {identity.email && <div className="room-dropdown-email">{identity.email}</div>}
                      </div>
                      <Link to="/profile" className="room-dropdown-link">
                        <div className="room-dropdown-item">
                          <span>👤</span>
                          <span>{t('singer.profile')}</span>
                        </div>
                      </Link>
                      {authUser && (
                        <button
                          type="button"
                          className="room-dropdown-link room-dropdown-button"
                          onClick={() => {
                            setDropdownOpen(false);
                            setShowStagePrefs(true);
                          }}
                        >
                          <div className="room-dropdown-item">
                            <span>🎤</span>
                            <span>{t('singer.stagePrefs.title')}</span>
                          </div>
                        </button>
                      )}
                      <Link
                        to="/"
                        className="room-dropdown-link"
                        onClick={(e) => {
                          e.preventDefault();
                          setDropdownOpen(false);
                          setShowLeaveConfirm(true);
                        }}
                      >
                        <div className="room-dropdown-item room-dropdown-item--danger">
                          <span>↪</span>
                          <span>{t('singer.leaveRoom')}</span>
                        </div>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </header>

            {joinError && <div className="room-error">{joinError}</div>}

            <div className="room-layout">
              <div className="room-layout__main">
                <SingerQueue
                  effectiveQueue={visibleQueue}
                  nowPlayingId={nowPlayingId}
                  identityId={identity.id}
                  roomId={roomId || ''}
                  waitTimeMessageId={waitTimeMessageId}
                  waitTimeLabel={waitEstimate !== null ? t('singer.waitTimeShort', { time: formatWaitTime(waitEstimate) }) : null}
                  favorites={favorites}
                  onRemoveSong={(messageId) => {
                    setOptimisticRemovals((prev) => new Set(prev).add(messageId));
                    socket.emit('delete_own_message', messageId);
                    if (roomId) trackEvent('song_removed', { roomId });
                  }}
                  onToggleFavorite={
                    isAuthenticated
                      ? (msg) => {
                          if (!msg.metadata?.videoId) return;
                          const isFav = favorites.some((f) => f.videoId === msg.metadata?.videoId);
                          toggleFavorite(
                            {
                              videoId: msg.metadata.videoId,
                              title: msg.metadata.title || msg.content,
                              thumbnail: msg.metadata.thumbnail || '',
                            },
                            !isFav
                          );
                        }
                      : undefined
                  }
                  onDuet={openDuetModal}
                />
              </div>

              <aside className="room-layout__side">
                <div className="room-controls-panel">
                  <div className="room-controls-title">{t('singer.yourSpot')}</div>
                  <div className="room-controls-group room-controls-group--column">
                    <div className="singer-spot-count">
                      {t('singer.songsInQueueCount', { count: mySongsCount, max: maxMessagesPerUser, plural: maxMessagesPerUser === 1 ? '' : 's' })}
                    </div>
                    {waitEstimate !== null && (
                      <div className="singer-wait-time">
                        {t('singer.waitTime', { time: formatWaitTime(waitEstimate) })}
                      </div>
                    )}
                    <div className="singer-spot-hint">
                      {isBlocked
                        ? t('singer.blockedHint')
                        : atLimit
                        ? t('singer.atLimitHint', { max: maxMessagesPerUser })
                        : t('singer.waitForTurn')}
                    </div>
                  </div>
                </div>
              </aside>
            </div>

            {!isBlocked && !isOwnSongOnStage && (nowPlayingId || queue.length > 0) && (
              <div
                className={`reaction-fab-group ${showSearch ? 'reaction-fab-group--hidden' : ''} ${
                  isQueueOpen && !atLimit ? '' : 'reaction-fab-group--low'
                }`}
              >
                {([
                  { type: 'heart' },
                  { type: 'star' },
                  { type: 'like' },
                ] as const).map((r) => (
                  <ReactionFabButton
                    key={r.type}
                    type={r.type}
                    onReact={() => {
                      const activeMessage = queue.find((m) => m.id === nowPlayingId) || queue[0];
                      if (activeMessage && roomId) {
                        socket.emit('reaction', { roomId, messageId: activeMessage.id, type: r.type as any });
                        trackEvent('reaction_sent', { roomId, type: r.type });
                      }
                    }}
                  />
                ))}
              </div>
            )}

            {isQueueOpen && !isBlocked && !atLimit && (
              <button
                className={`fab-add ${showSearch ? 'fab-add--hidden' : ''}`}
                aria-label={t('singer.addSong')}
                onClick={() => setShowSearch(true)}
              >
                +
              </button>
            )}

            <SearchSheet
              open={showSearch}
              onClose={() => {
                setShowSearch(false);
                setQuickSong('');
                setYtResults([]);
                setYtFromCache(false);
                setYtError(null);
                setPreviewVideo(null);
                setSelectedArtist(null);
                setArtistSongs(null);
              }}
              quickSong={quickSong}
              setQuickSong={setQuickSong}
              ytResults={ytResults}
              isSearching={isSearching}
              ytError={ytError}
              artists={artists}
              selectedArtist={selectedArtist}
              artistSongs={artistSongs}
              isLoadingArtistSongs={isLoadingArtistSongs}
              onSelectArtist={handleSelectArtist}
              onClearArtist={handleClearArtist}
              onPreview={setPreviewVideo}
              onAdd={addVideo}
              isAuthenticated={isAuthenticated}
              favorites={favorites}
              playlists={playlists}
              savedSongs={savedSongs}
              resultsFromCache={ytFromCache}
              onForceYouTubeSearch={handleForceYouTubeSearch}
              forceDisabled={forceUsedQuery === quickSong.trim()}
              onToggleFavorite={(video, isFavorite) => toggleFavorite(video, isFavorite)}
            />
          </div>
        </div>
      </div>

      {previewVideo && (
        <div className="video-preview-overlay" onClick={() => setPreviewVideo(null)}>
          <div className="video-preview-card" onClick={(e) => e.stopPropagation()}>
            <button className="video-preview-close" onClick={() => setPreviewVideo(null)}>
              ✕
            </button>
            <div className="video-preview-title">{previewVideo.title}</div>
            <div className="video-preview-frame">
              <img
                src={getSanitizedYouTubeThumbnail(previewVideo.videoId) || previewVideo.thumbnail}
                alt={previewVideo.title}
                className="video-preview-thumb"
              />
            </div>
            <div className="video-preview-actions">
              <button className="btn-primary" onClick={() => addVideo(previewVideo)}>
                {t('singer.addToQueue')}
              </button>
            </div>
          </div>
        </div>
      )}

      <DuetInviteModal
        isOpen={showDuetModal}
        singers={duetSingers.filter((s) => s.userId !== identity.id)}
        onClose={() => {
          setShowDuetModal(false);
          setSelectedDuetSongId(null);
        }}
        onInvite={sendDuetInvite}
      />

      {authUser && (
        <StagePrefsModal
          isOpen={showStagePrefs}
          userId={authUser.id}
          fallbackName={authUser.name}
          onClose={() => setShowStagePrefs(false)}
          onSaved={(savedName) => {
            if (savedName) setDisplayName(savedName);
          }}
        />
      )}

      <div className="duet-toast-container">
        {duetToasts.map((invite) => (
          <DuetInviteToast
            key={invite.inviteId}
            invite={invite}
            onAccept={acceptDuet}
            onDecline={declineDuet}
            onExpire={removeDuetInvite}
          />
        ))}
        {duetResults.map((result) => (
          <DuetResultToast key={result.id} result={result} onDone={removeDuetResult} />
        ))}
        {turnNotices.map((notice) => (
          <TurnToast key={notice.id} notice={notice} onDone={removeTurnNotice} />
        ))}
      </div>

      {showLeaveConfirm && (
        <div
          className="video-preview-overlay"
          onClick={() => setShowLeaveConfirm(false)}
        >
          <div
            className="room-modal-card room-modal-card--centered"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="room-modal-title">{t('singer.leaveRoomConfirmTitle')}</h2>
            <p className="room-modal-lead">
              {t('singer.leaveRoomConfirmMessage')}
            </p>
            <div className="room-modal-actions">
              <button
                className="btn-secondary btn-flex"
                onClick={() => setShowLeaveConfirm(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn-danger btn-flex"
                onClick={() => {
                  setShowLeaveConfirm(false);
                  leave();
                  navigate('/');
                }}
              >
                {t('singer.confirmLeaveRoom')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface SingerQueueProps {
  effectiveQueue: QueueMessage[];
  nowPlayingId: string | null;
  identityId: string;
  roomId: string;
  waitTimeMessageId?: string | null;
  waitTimeLabel?: string | null;
  favorites: VideoResult[];
  onRemoveSong: (messageId: string) => void;
  // Só definido para utilizadores autenticados — sem isto a estrela de
  // "save song" nem aparece no QueueItem.
  onToggleFavorite?: (msg: QueueMessage) => void;
  onDuet: (messageId: string) => void;
}

// Lista única: a queue global (ou só as próprias músicas + now-playing, quando
// o host desliga "show full queue to singers"). As músicas do próprio singer
// (e dos seus duetos) destacam-se com borda (--mine) e têm botão de apagar. A
// secção separada "Your songs" foi removida após o evento 12/10.
function SingerQueue({ effectiveQueue, nowPlayingId, identityId, roomId, waitTimeMessageId, waitTimeLabel, favorites, onRemoveSong, onToggleFavorite, onDuet }: SingerQueueProps) {
  const { t } = useI18n();
  const nowPlaying = effectiveQueue.find((m) => m.id === nowPlayingId) || null;
  const isDuetPartner = (m: QueueMessage) => !!m.partnerIds && m.partnerIds.includes(identityId);
  const queueSongs = effectiveQueue.filter((m) => m.id !== nowPlayingId);

  const displayQueue = useMemo(
    () => effectiveQueue.map((m) => ({ kind: 'song' as const, data: m })),
    [effectiveQueue]
  );

  const renderSong = (msg: QueueMessage, { isOnStage = false, isMine = false, highlight = false }: { isOnStage?: boolean; isMine?: boolean; highlight?: boolean } = {}) => (
    <QueueItem
      key={msg.id}
      msg={msg}
      idx={displayQueue.findIndex((m) => m.data.id === msg.id)}
      queue={displayQueue}
      isHost={false}
      isOwner={isMine}
      roomId={roomId}
      isNowPlaying={isOnStage}
      highlight={highlight || isMine}
      onRemoveMoment={undefined}
      onRemove={() => onRemoveSong(msg.id)}
      disableRemove={!!(msg as any).optimistic}
      isFavorite={favorites.some((f) => f.videoId === msg.metadata?.videoId)}
      onToggleFavorite={onToggleFavorite ? () => onToggleFavorite(msg) : undefined}
      canDuet={isMine && !(msg as any).optimistic}
      onDuet={() => onDuet(msg.id)}
      partnerNames={msg.partnerNames}
      waitTimeLabel={waitTimeMessageId === msg.id ? waitTimeLabel ?? undefined : undefined}
    />
  );

  if (effectiveQueue.length === 0) {
    return <div className="room-empty">{t('singer.emptyQueue')}</div>;
  }

  return (
    <div className="singer-queue">
      {nowPlaying && (
        <div className="singer-queue-section singer-queue-section--stage">
          <div className="singer-queue-section__title">🎤 {t('singer.onStage')}</div>
          {renderSong(nowPlaying, { isOnStage: true, isMine: nowPlaying.userId === identityId })}
        </div>
      )}

      {queueSongs.length > 0 && (
        <div className="singer-queue-section singer-queue-section--queue">
          <div className="singer-queue-section__title">
            {t('singer.queueTitle')} <span className="singer-queue-count">{queueSongs.length}</span>
          </div>
          {queueSongs.map((msg) =>
            renderSong(msg, {
              isMine: msg.userId === identityId,
              highlight: msg.userId === identityId || isDuetPartner(msg),
            })
          )}
        </div>
      )}
    </div>
  );
}

function ReactionFabButton({ type, onReact }: { type: 'heart' | 'star' | 'like'; onReact: () => void }) {
  const { t } = useI18n();
  const [popping, setPopping] = useState(false);
  const [floater, setFloater] = useState<{ id: number; type: 'heart' | 'star' | 'like' } | null>(null);
  const idRef = useRef(0);
  return (
    <button
      className={`reaction-fab-btn ${popping ? 'reaction-fab-btn--popped' : ''}`}
      onClick={() => {
        if (popping) return;
        onReact();
        setPopping(true);
        const id = ++idRef.current;
        setFloater({ id, type });
        setTimeout(() => setPopping(false), 200);
        setTimeout(() => setFloater((curr) => (curr?.id === id ? null : curr)), 900);
      }}
      aria-label={t(`singer.reaction.${type}`)}
      title={t(`singer.reaction.${type}`)}
    >
      <ReactionIcon type={type} />
      {floater && (
        <span key={floater.id} className="reaction-floater" aria-hidden="true">
          <ReactionIcon type={floater.type} />
        </span>
      )}
    </button>
  );
}
